import useUser from "@/api/apis";
import { cn } from "@/libs/utils";
import { useWelcomeCouponIssuing } from "@/hooks/useWelcomeCouponIssuing";
import { Button } from "@/v2-components/ui/button";
import { useTrans } from "@/utils/useTrans";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGeRouted } from "@/hooks/useGeRouted";
import { CheckoutChoiceDialog } from "@/components/tactsuit/components/buy-tactsuit/checkout-choice-dialog";

/**
 * 게스트로 **확정**됐나 — 결제 전에 로그인/게스트 선택지를 물어야 하나.
 *
 * 이름은 결제 버튼이 비회원/회원 **둘로 갈리던**(#5101) 때의 것이다. #5293 부터 버튼은 늘
 * 하나(`Checkout`)이고, 이 값이 참이면 누를 때 선택 모달이 뜬다(아래 로그아웃 분기 주석).
 * 그때 옆 `Add to Bag` 도 이 값을 따라 40px 아이콘으로 줄던 것(#5317)을 멈췄다 — 지금은 늘
 * 라벨이고, `TactSuitCard` 의 `iconOnly` 능력만 남아 있다. PDP 호출처는 이 값을 읽지 않는다.
 *
 * ⚠️ **호출처가 `useUser()` 로 같은 판정을 다시 하지 말 것.** 다시 이 값으로 옆 버튼의 모양을
 * 정하는 날(`iconOnly`) 이 컴포넌트의 분기와 어긋나면 아이콘 1개 + 결제 1개(빈 자리) 같은 조합이
 * 나온다. 같은 술어를 두 곳에서 계산해 어긋난 사고가 이 리포에 반복돼 있다(docs/pitfalls.md #27).
 * 그래서 아래 컴포넌트도 이 훅을 통해 분기한다 — 정의가 한 곳뿐이어야 어긋날 수 없다.
 */
export const useIsSplitCheckout = () => {
  const { user, isLoading } = useUser();
  // 인증 확인 중에는 **단일 버튼**을 유지한다(아래 분기 주석의 깜빡임 사유와 동일).
  return !isLoading && !user;
};

/**
 * 제품 상세(PDP)의 결제 버튼.
 *
 * 기존에는 로그아웃 상태에서도 '결제하기' 하나만 있어서, 비회원 결제로 바로 넘어가는 것 외의
 * 선택지가 없었다. 장바구니(`cart-checkout-button-ui`)와 동일하게 비회원/회원 결제를 나눠
 * 제시해 회원 결제라는 선택지를 노출한다. 로그인 상태에서는 기존과 같이 버튼 하나.
 *
 * 버튼 라벨은 장바구니와 같은 `gnb` 네임스페이스 키를 재사용한다.
 */
const PdpCheckoutButtons = ({
  isCheckingOut,
  disabled,
  onGuestCheckout,
  onAccountCheckout,
  className,
}: {
  isCheckingOut?: boolean;
  disabled?: boolean;
  onGuestCheckout: (e: any) => void;
  onAccountCheckout: (e: any) => void;
  className?: string;
}) => {
  const { t } = useTrans(["gnb"]);
  // ★ 분기(선택 모달을 띄우나)는 위 훅 하나로만 판정한다 — 정의가 한 곳이어야 어긋나지 않는다.
  const isSplitCheckout = useIsSplitCheckout();
  const [choiceOpen, setChoiceOpen] = useState(false);
  // 이 컴포넌트는 이미 `useUser()`(react-query)를 쓰므로 provider 안이다 — 훅을
  // provider-free 로 유지하면서 캐시 갱신은 여기서 한다.
  const queryClient = useQueryClient();
  // GE 국제 체크아웃으로 가는 카트인지. 술어는 `isGeRoutedFor` 공유(얇은 껍데기 훅).
  const isGeRouted = useGeRouted();
  // ⚠️ `isError`·`isTokenLoading` 는 **우리 쪽(#5194) 가드다 — develop 판에는 없다.**
  // develop 의 `isDisabled` 는 `disabled || isCheckingOut` 뿐이라, 그대로 받으면
  // **토큰 미확정 구간에 결제가 열려 회원 주문이 게스트 주문이 된다**(아래 주석 참조).
  // 충돌을 develop 쪽으로 풀면 조용히 사라지는 종류다(docs/pitfalls.md #27).
  const { isLoading, isError, isTokenLoading } = useUser();
  // **발급 중일 때만** 가입 유도 배지를 띄운다 — 플래그만 보면 발급 마감 후에도 배지가
  // 남는다. develop 이 플래그 → 발급 상태 훅으로 바꿨고(#5246), 그쪽을 따른다.
  const welcomeOffer = useWelcomeCouponIssuing();

  // 인증이 **확정되지 않은** 두 창. 둘 다 `user === undefined` 라 구분 없이 "게스트" 로
  // 확정하면 회원에게 가입 유도가 뜬다. `isError` 는 `isPending` 이 **false** 라 놓치기 쉽다.
  const authUnconfirmed = isLoading || isError;

  // 배지는 "쓸 수 없는 혜택 약속" 이므로 두 창 모두에서 숨긴다.
  // (`isError` 창은 아래 `if (isLoading || user)` 에서 **게스트 분기로 떨어져** 배지가
  //  실제로 떴다 — #5194 2차 리뷰에서 프로브로 확인.)
  //
  // ⚠️ `isError` 는 그 사용자에게 **영구**일 수 있으므로(아래 `isDisabled` 주석) 배지도
  // 영구히 숨는다. 그 사용자는 사이트가 회원으로 인식할 수 없는 **실질 게스트**, 즉 가입
  // 쿠폰 캠페인의 정확한 타깃인데 캠페인을 한 번도 못 본다. 그 비용이 "회원에게 못 쓸
  // 혜택을 약속" 하는 리스크보다 작다고 보고 **수용한다**(#5194 3차 리뷰).
  // 같은 "영구" 라는 사실이 규칙 1(숨김)과 규칙 2(잠그지 않음)에서 반대로 풀리는 게
  // 의아할 수 있는데, 의도된 것이다 — 한쪽은 표시, 한쪽은 구매 가능 여부라 비용이 다르다.
  // 배지의 노출 조건과 표시 값을 **한 변수**로 묶는다 — 조건(boolean)과 값(offer)을 따로
  // 들면 "조건은 참인데 값이 없다" 는 조합이 타입상 가능해지고, 그때 화면에 percent 가
  // 비거나 0 으로 찍힌다. null 이면 렌더하지 않는다.
  const welcomeBadgeOffer = authUnconfirmed ? null : welcomeOffer;

  // ★ 토큰 첫 조회가 끝날 때까지 결제를 막는다.
  // `onGuestCheckout`·`onAccountCheckout` **둘 다** 누른 시점에 토큰을 다시 읽어 draft 를
  // 만든다(`hooks/shops/useCheckout.ts` 의 `resolveCheckoutToken`). 그런데 그 재조회는 3초에서
  // 끊고 첫 조회는 15초까지 기다린다 — 느린 회선에서 첫 조회 중에 누르면 재조회가 먼저 끊기고
  // 캐시도 비어 있어 회원 주문이 빈 authorization 으로 **게스트 주문**이 되고 쿠폰도 날아간다 —
  // 에러 없이 조용히. 아래 로딩 폴백이 단일 `Checkout` 을 primary 로 세우므로 회원이 가장 누르기
  // 쉬운 자리에 그 경로가 있었다(#5194 리뷰 1, 이 컴포넌트는 #5101 부터 prod 노출).
  // 첫 조회가 **실패로** 끝난 뒤(모름)는 잠그지 않는다 — 그 구간은 클릭 재조회가 가른다
  // (docs/pitfalls.md #70 — `useUser().token` 은 셋이다).
  //
  // ⚠️ **`authUnconfirmed` 를 쓰지 않는다 — `isError` 제외는 의도적이다.**
  // `isError` 는 auth 호스트로의 `fetch` 자체가 거부될 때만 난다(`fetchUser` 는 `!res.ok`·
  // 빈 body·비JSON 을 전부 `null`=게스트로 처리한다). 현실적 원인은 **애드블로커/프라이버시
  // 확장이 `auth.bhaptics.com` 을 차단**하는 경우이고, 이건 일시적 장애가 아니라 그 사용자
  // 에게 **영구**다. 여기를 잠그면 그 사용자는 **영영 결제할 수 없다.**
  // 게다가 auth 가 막힌 사용자는 로그인 자체가 불가하므로 **게스트 주문이 올바른 결과**다.
  // ⚠️ 잔여 손실은 "일시" 가 아니다. 애드블로커는 보통 fetch/XHR 을 막지만 **최상위
  // 내비게이션은 막지 않으므로**, 그 사용자가 로그인 버튼으로 auth 호스트에 가면 세션
  // 쿠키는 실제로 생긴다. 돌아와도 `/api/user` fetch 는 계속 차단되니 사이트는 그를
  // 영원히 게스트로 본다 → **그 사람의 모든 주문이 게스트로 귀속되고 회원 쿠폰을 못 받는다.**
  // 그래도 (b)가 맞다 — 잠그면 그 사용자는 결제 자체를 못 하니 훨씬 나쁘다.
  // → 매출·구매 자체를 막는 손해 vs 상시 귀속 손실 로 후자를 택한다(#5194 3차 리뷰).
  const isDisabled = disabled || isCheckingOut || isLoading || isTokenLoading;
  // ★주 버튼은 **옵션 선택 여부와 무관하게 항상 채움**이다(#5283).
  //
  // 종전엔 `hasSku ? "blue-fill" : "blue-outline"` 로, 필수 옵션이 미선택이면 배경을
  // 지워 "덜 눌러도 되는 버튼" 처럼 보이게 했다. 그런데 이 버튼은 **그때도 눌린다** —
  // `hasSku` 는 `isDisabled` 에 들어간 적이 없고, 누르면 `validateSku()` 가 미선택 영역을
  // 강조하고 거기로 스크롤한다(`BuyTactsuitDetailPageTemplate`). 즉 겉모습만 "비활성처럼"
  // 이었고 실제 안내 경로는 이미 있었다 — 사용자는 누를 수 있는 버튼을 못 누르는 것으로
  // 읽었다(실측: `tactglove-dk3` 처럼 필수 사이즈가 있는 PDP 에서 `Account Checkout`
  // 배경이 투명).
  //
  // ⚠️ 되돌릴 때 주의: 배경을 다시 조건부로 만들려면 **누르면 무슨 일이 일어나는지**부터
  // 바꿔야 한다. 지금은 "항상 눌리고, 부족하면 그 자리를 알려준다" 가 한 쌍이다.
  const primaryVariant = "blue-fill";

  // ★잠금이 걸리면 모달을 닫는다 — **표시가 아니라 의도 상태(`choiceOpen`)를** 되돌린다.
  //
  // 결제 진입점이 1개 → 3개로 늘었으므로(바깥 `Checkout` · 모달 `Sign In` · 모달
  // `Continue as Guest`) `isDisabled`(#5194 토큰 잠금)가 모달 안까지 미쳐야 한다.
  //
  // ⚠️ **`open={choiceOpen && !isDisabled}` 로 표시만 파생시키면 안 된다** — 소스인
  // `choiceOpen` 이 참으로 남아 잠금이 풀리는 순간 **사용자가 누르지 않은 모달이 다시 뜬다**
  // (실측: 열림 → `isCheckingOut=true` 닫힘 → `false` **다시 열림**. 리뷰 R2 지적).
  // 잠금 누락은 "안전 쪽으로 실패" 지만 이건 반대 방향이라 성격이 다르다 — ★교체 지점에
  // 인라인 인증 폼이 들어오면 입력 중이던 폼이 사라졌다 되살아나는 자리다.
  //
  // ⚠️ **파생식(`open={choiceOpen && !isDisabled}`)과 이 효과를 같이 두지 않는다.**
  // 처음엔 *"효과는 페인트 뒤에 흘러서 잠금이 켜진 그 한 프레임을 파생식이 덮는다"* 는
  // 이유로 둘 다 뒀는데, **두 방어가 서로의 뮤턴트를 가린다.** 4조합 전수 실측:
  //
  //   둘 다 있음 ① 파생식 제거          → 17 passed  ← 효과가 3단계를 덮는다
  //             ② 효과 조건 반전        → 17 passed  ← 파생식이 2단계를 덮는다
  //             ③ 효과 통째 제거        →  1 failed
  //             ④ deps 를 `[]` 로       →  1 failed
  //   효과 단독 ③ 효과 제거 / ② 반전 / ④ deps  →  각 2 failed
  //
  // 정확히 말하면 **가림은 부분적**이다 — 효과의 *존재*는 잠겼고(③④) **파생식은 통째로
  // 지워도 안 잡히며 효과의 *조건*도 안 잡힌다**(①②). 그래도 결론은 같다: 둘 중 검증되는
  // 것은 효과뿐이고, 파생식은 있으나 없으나 테스트가 구별하지 못한다.
  //
  // 효과 하나만 두면 셋 다 잡힌다. 잃는 것은 잠금이 켜진 그 한 프레임인데, 실제 경로에서는
  // 모달 버튼 핸들러가 같은 클릭에서 `setChoiceOpen(false)` 를 하므로 같은 커밋에 닫힌다 —
  // 검증할 수 없는 방어를 남기는 값보다 작다. (★"프레임 보호" 를 이유로 파생식을 되돌리려면
  // 위 ①이 잡히게 만드는 케이스를 먼저 붙일 것. jsdom 은 `act()` 가 효과를 동기로 흘려서
  // 그 프레임이 관측되지 않으므로, jest 로는 그 케이스를 쓸 수 없다.)
  useEffect(() => {
    if (isDisabled) setChoiceOpen(false);
  }, [isDisabled]);

  // 인증 확인 중에는 **기존 동작(단일 결제 버튼)** 을 유지한다. `user` 만 보면 로딩 창에서
  // undefined 라 회원에게도 비회원/회원 분리 버튼이 잠깐 보이고, 이 컴포넌트는 장바구니 시트와
  // 달리 **PDP 진입마다 렌더**되므로 그 깜빡임이 매번 노출된다.
  // (게스트는 로딩이 끝난 뒤 분리 버튼으로 바뀐다 — 회원에게 잘못된 CTA 를 보여주는 것보다
  //  게스트에게 선택지를 조금 늦게 주는 편이 낫다.)
  //
  // ⚠️ `/cart` 레일(`cart-sheet/cart-checkout-button-ui.tsx`)은 **반대로** 로딩 중 게스트
  // 레이아웃을 유지한다. 두 곳이 갈리는 이유:
  //   - 여기는 **로딩 창**에서 단일 버튼이라 배지가 안 뜬다. 단 **`isError` 창은 아래
  //     조건이 게스트 분기로 떨어뜨리므로** 배지 게이트는 `authUnconfirmed` 로 따로 본다
  //     (여기 조건을 `isError` 까지 넓히지 않는 이유: 그러면 단일 `Checkout` 이 되는데
  //      그 버튼도 `onGuestCheckout` 이라 결국 같은 토큰 경로다 — 얻는 게 없다)
  //   - 카트 레일은 `Account Checkout` 이 **그 화면의 유일한 로그인 진입점**이라 접으면 사라진다
  //   - PDP 는 진입마다 렌더돼 깜빡임 노출 빈도가 훨씬 높다
  // 공통 규칙은 "토큰 미확정 동안 결제 잠금"(위 `isDisabled`)이고, 레이아웃만 다르다.
  if (!isSplitCheckout) {
    return (
      <Button
        size="sm"
        shape="pill"
        variant={primaryVariant}
        disabled={isDisabled}
        onClick={onGuestCheckout}
        className={cn("px-6", className)}
      >
        {t("Checkout")}
      </Button>
    );
  }

  // 로그아웃: 결제 버튼은 **하나**이고, 누르면 로그인/게스트를 고르는 모달이 뜬다
  // (#5293, 시안 29435:37715). 종전에는 버튼을 둘로 갈라 표현했는데(#5101) 그러면 CTA 행에
  // 버튼이 3개가 되어 시안의 `Checkout` 1개 구조와 리테일러 아이콘 자리가 성립하지 않는다.
  //
  // ⚠️ 갈림이 사라져도 `useIsSplitCheckout` 은 남는다 — 이제 뜻이 "결제가 둘로 갈리나" 가
  //    아니라 **"로그인 선택지를 물어야 하나"** 다. 옆 `Add to Bag` 의 모양은 이 값과
  //    무관해졌다(라벨 유지). 술어 정의가 한 곳이라는 성질은 그대로 지킨다.
  return (
    <div className={cn("relative", className)}>
      {/* 신규 가입 유도 배지. **로그아웃일 때만** 이 분기에 오므로 노출 대상·빈도가
          종전(`Account Checkout` 위)과 같다. 회원에게는 애초에 안 뜬다.
          조건과 값은 `welcomeBadgeOffer` 하나를 공유한다 — 각자 계산하면 갈린다. */}
      {welcomeBadgeOffer && (
        <span className="pointer-events-none absolute -top-2 right-0 z-10 whitespace-nowrap rounded-full bg-bhaptics-yellow px-2 py-[2px] text-[11px] font-600 leading-tight text-coolgray-90 shadow-sm laptop:-top-2.5 laptop:px-2.5 laptop:text-xs">
          {t("Sign up & save {{percent}}%", {
            percent: welcomeBadgeOffer.percent,
          })}
        </span>
      )}
      <Button
        size="sm"
        shape="pill"
        variant={primaryVariant}
        disabled={isDisabled}
        onClick={() => setChoiceOpen(true)}
        className="w-full px-6"
      >
        {t("Checkout")}
      </Button>
      {/* ★결제 진입점이 **1개에서 3개**로 늘었다(바깥 `Checkout` · 모달 `Sign In` · 모달
          `Continue as Guest`). 위 `isDisabled` 는 바깥 버튼 하나에만 걸려 있었으므로,
          **모달 안의 두 경로에도 같은 잠금을 적용한다** — 두 버튼도 결국 `onGuestCheckout`·
          `onAccountCheckout` 를 부르고 그 둘이 토큰을 다시 읽어 draft 를 만든다(위 `isDisabled` 주석).
          #5348 에서 `router.push` 를 공유 훅→호출처로 내리며 **효과 쪽이 안 잠긴** 것과
          같은 자리다: 표면을 옮겼으면 그 표면의 보호도 옮겨야 한다.

          ⚠️ **오늘 이 잠금이 실제로 무엇을 막는지는 좁다** — 정직하게 적어 둔다:
            · `startCheckout` 자체가 `if (isCheckingOut || !countryCode) return` 로 재진입을
              막는다(`hooks/shops/useCheckout.ts:240`, `:323`) → 이중 제출은 이미 닫혀 있다
            · 이 분기는 `useIsSplitCheckout`(= `!isLoading && !user`)이 참일 때만 오므로
              **모달을 보는 사람은 정의상 게스트**다. 토큰 미확정으로 회원 주문이 게스트가
              되는 #5194 의 시나리오는 회원에게 일어나는 일이라 이 경로엔 닿지 않는다
          즉 지금 prod 결함을 막는 게 아니라 **불변식의 대칭**을 맞추는 것이다. 여기가
          비대칭이면 ★교체 지점(`AuthInline` → 인라인 폼/팝업 인증)에서 잠금이 조용히 빠진다.

          잠금은 위 `useEffect` **한 곳**이 담당한다 — 대안 셋을 다 재보고 버렸다:
            · 버튼만 `disabled` → 눌러도 아무 일이 안 나는 **죽은 클릭**이 된다
            · `disabled` 를 자식에 prop 으로 내림 → ★교체된 폼이 그걸 존중할 **의무를
              기억해야** 한다. 잠금이 빠지는 자리를 옮기는 것뿐이다
            · 핸들러에 `if (isDisabled) return` **이중 잠금** → 모달이 닫힌 상태에서는 그 버튼이
              존재하지 않으니 **UI 로 도달할 수 없는 방어**이고, 도달 못 하니 테스트로 잠글 수도
              없다 → 넣지 않는다(초록 테스트만 남으면 허위 확신) */}
      <CheckoutChoiceDialog
        open={choiceOpen}
        onClose={() => setChoiceOpen(false)}
        // **팝업을 쓸 수 없을 때만 타는 폴백**(모바일 · 팝업 차단). 페이지가 떠나므로
        // 기존 계약(구성 담기 → auth 이동)이 그대로 필요하다 — PDP 옵션 스토어는
        // persist 가 꺼져 있어 왕복에 선택이 날아간다.
        onSignIn={(e?: any) => {
          setChoiceOpen(false);
          onAccountCheckout(e);
        }}
        // 팝업 로그인 완료 → **게스트와 같은 결제 경로**를 태운다. 회원 여부는 결제 실행
        // 시점의 토큰 해석이 정하므로, 여기서 다른 함수를 쓰면 선택과 집행이 갈린다.
        // 담기 선행이 없는 이유: 팝업이라 페이지를 떠나지 않아 PDP 옵션이 살아 있다.
        //
        // ★검증된 user 를 캐시에 먼저 심는다. `handleCheckout` 에는 결제로 바로 가지 않는
        // 조기 반환 분기가 있다(SKU 미선택 → validateSku · 품절+빈카트 → showCart ·
        // 국가 미확정 → return). 그 경우 페이지에 남는데 `useUser()` 가 여전히 비로그인이면
        // `useIsSplitCheckout` 이 참이라 **방금 로그인한 사용자에게 모달이 다시 뜨고**
        // `Sign up & save {{percent}}%` 배지도 계속 노출된다(#5194 3차 리뷰가 피하려던 상태).
        onAuthenticated={(user) => {
          // ⚠️ **GE 경로에서는 캐시를 건드리지 않는다.**
          //
          // `isLoggedIn` 전이는 `_app` 의 `CartLoginSync` 를 발화시킨다. 그 게이트
          // (`shouldClaimInsteadOfMerge`)는 GE 페이지이거나 결제 진행 표식이 있을 때만 claim 이라, 첫 GE 진입
          // **전**인 여기(PDP)에서는 **else 분기 = `mergeGuestCart()`** 를 탄다 — 그건 **게스트 문서를 삭제**하므로
          // GE 가 카트를 못 읽는다(`CartApi.mergeGuestCart` 주석의 금지 사항).
          // 직후 `writeGuestCartForCheckout` 이 다시 쓰지만 둘 다 락 없는 fire-and-forget
          // 이라, 삭제가 나중에 도착하면 GE 가 빈 카트를 본다.
          //
          // 그 게이트는 "로그인은 항상 full-nav" 를 전제로 만들어졌다. 팝업 로그인이 그
          // 전제를 깨므로, GE 경로에서는 전이를 일으키지 않고 full-nav 후 재마운트에
          // 맡긴다 — `/international-checkout` 이 마운트되면 pathname 이 맞아
          // `claimGuestCartOwner()`(문서 삭제 없이 소유자만 기록)가 정상 발화한다.
          //
          // 비-GE 경로는 캐시를 갱신해야 한다. draft 는 items 를 body 로 실어 보내
          // 서버 카트를 읽지 않으므로 merge 와 경합하지 않고, 갱신하지 않으면 결제로
          // 바로 가지 않는 조기 반환 분기(SKU 미선택·품절·국가 미확정)에서 방금 로그인한
          // 사용자에게 모달이 다시 뜬다.
          if (!isGeRouted) {
            queryClient.setQueryData(["/api/user"], user);
          }
          setChoiceOpen(false);
          onGuestCheckout(undefined);
        }}
        onGuest={(e?: any) => {
          setChoiceOpen(false);
          onGuestCheckout(e);
        }}
      />
    </div>
  );
};

export default PdpCheckoutButtons;
